import { Button } from '@/components/ui/button';
import { useCreateEvent } from '@/features/events/hooks/useCreateEvent';
import { EventType } from '@/features/events/types/EventType';
import { ParameterData, parameterStatusUtils } from '@/features/ocr/types/ParameterData';
import { LoadingOverlay } from '@/features/shared/components/Loaders';
import { DownloadIcon, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ParameterCard } from '../components/ParameterCard';
import { useDeleteDocument } from '../hooks/useDeleteDocument';
import { useDownloadDocument } from '../hooks/useDownloadDocument';
import { useGetDocumentDetail } from '../hooks/useGetDocumentDetail';
import { useGetDocuments } from '../hooks/useGetDocuments';

const SingleReport = () => {
  const query = new URLSearchParams(useLocation().search);
  const id = query.get('id');
  const { sendEvent } = useCreateEvent()
  if (!id || id === null) {
    <div>Errore: documento non trovato</div>;
  }
  const {
    data: currentDocumentData,
    isLoading: isLoadingDetails,
    error: isErrorDetails,
  } = useGetDocumentDetail(id!);
  const { data: documents, isLoading, error } = useGetDocuments();

  const {
    mutate: downloadDocument,
    isPending: isLoadingDownload,
    isSuccess: isSuccessDownload,
    data: document,
  } = useDownloadDocument(id!);
  const {
    mutate: deleteDocument,
    isPending: isLoadingDelete,
    error: isErrorDelete,
    isSuccess: isSuccessDelete,
  } = useDeleteDocument(id!);

  const handleDownloadDocument = async () => {
    downloadDocument();
    await sendEvent({
      event: EventType.BUTTON_CLICK,
      sub_category: "action_button",
      event_metadata: { action: "download_report", document_id: id },
    })
  };

  const handleDeleteDocument = async () => {
    deleteDocument();
    await sendEvent({
      event: EventType.BUTTON_CLICK,
      sub_category: "action_button",
      event_metadata: { action: "delete_report", document_id: id },
    })
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (isSuccessDelete) {
      toast.success('Documento eliminato con successo!');
      navigate('/reports');
    }

    if (isErrorDelete) {
      toast.error('Errore durante l\'eliminazione del documento');
    }
  }, [isSuccessDelete, isErrorDelete, navigate]);

  useEffect(() => {
    if (isSuccessDownload && document) {
      window.open(document.url, '_blank');
    }
  }, [isSuccessDownload, document]);

  useEffect(() => {
    void sendEvent({
      event: EventType.PAGE_VIEW,
      sub_category: "report_page",
      event_metadata: { document_id: id }
    })
  }, [sendEvent, id])

  if (isLoading || isLoadingDetails) {
    return (
      <div className="relative w-full h-full">
        <LoadingOverlay />
      </div>
    )
  }

  if (error || isErrorDetails) {
    return <div>Errore durante il caricamento</div>;
  }

  const currentDocument = documents?.find((el) => el.id === id);

  if (!currentDocumentData || !currentDocument) {
    return <div>Documento non trovato</div>;
  }
  const docDate = currentDocument!.createdAt;
  const docLab = currentDocument!.labName;

  const docNumberOfMarkers = currentDocumentData.length;

  const onParameterInfoClick = async (parameter: ParameterData) => {
    navigate(
      `/parameter-info?parameter=${parameter.name.replace(
        '_percentage',
        '',
      )}`,
    )
    await sendEvent({
      event: EventType.CLICK,
      sub_category: "on_parameter_info_click",
      event_metadata: { parameter: parameter.name },
    })
  }

  // TODO [TM]: In the future remove filter
  const realCurrentDocumentData = currentDocumentData.filter(data => data.name !== "bas")

  return (
    <div className='w-full relative space-y-sm pb-4'>
      <div className='flex justify-between'>
        <div className='space-y-[5px]'>
          <div className='font-bold text-2xl leading-small'>
            {docDate}<span className='font-normal'> | {docLab}</span>
          </div>
          <div className='text-base'>
            {docNumberOfMarkers} markers
          </div>
        </div>
        <div>
          <div className='flex flex-col gap-2 md:flex-row'>
            <Button
              onClick={handleDownloadDocument}
              disabled={isLoadingDownload}
            >
              <DownloadIcon />
              Download
            </Button>
            <Button
              variant={'destructive'}
              onClick={handleDeleteDocument}
              disabled={isLoadingDelete}
            >
              <Trash2 />
              Delete
            </Button>
          </div>
        </div>
      </div>
      {realCurrentDocumentData.map((parameter, index) => (
        <ParameterCard
          key={index}
          data={parameter}
          description={parameterStatusUtils.getGeneralNotionForId(
            parameter.name,
          )}
          onInfoClick={() => onParameterInfoClick(parameter)}
        />
      ))}
    </div>
  );
};

export default SingleReport;
